import { ArchiveRestoreIcon, BookmarkIcon, BookmarkPlusIcon, Trash2Icon } from "lucide-react";
import { useStore } from "zustand";

import { releaseUnreferencedComposerFile } from "../../composerFileAttachmentLifecycle";
import { promptStashStore, type PromptStashEntry } from "../../promptStashStore";
import { ComposerControl, ComposerControlChevron, ComposerControlIcon } from "./ComposerControl";
import { composerFloatingLayerProps } from "./composerEventScope";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuShortcut,
  MenuTrigger,
} from "../ui/menu";

export interface ComposerStashControlProps {
  readonly disabled: boolean;
  readonly onResult: (message: string, type: "error" | "success" | "warning") => void;
  readonly shortcutLabel?: string | null | undefined;
  readonly open?: boolean | undefined;
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  readonly adapter: {
    readonly save: () => void;
    readonly restore: (entry: PromptStashEntry) => void;
  };
}

export const COMPOSER_STASH_MENU_POPUP_CLASS =
  "w-[min(20rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)]";

/** Whether the toolbar has anything to show: nothing stashed means nothing to browse. */
export function useHasStashEntries(): boolean {
  return useStore(promptStashStore, (state) => state.entries.length > 0);
}

export function ComposerStashToolbarControl(props: ComposerStashControlProps) {
  const entries = useStore(promptStashStore, (state) => state.entries);
  if (entries.length === 0) return null;
  return (
    <Menu open={props.open} onOpenChange={props.onOpenChange}>
      <MenuTrigger
        render={
          <ComposerControl
            type="button"
            disabled={props.disabled}
            aria-label={`Prompt stash${entries.length > 0 ? `, ${entries.length} saved` : ""}`}
            title="Prompt stash"
          />
        }
      >
        <ComposerControlIcon icon={BookmarkIcon} />
        <span>Stash</span>
        {entries.length > 0 ? (
          <span className="text-muted-foreground">{entries.length}</span>
        ) : null}
        <ComposerControlChevron />
      </MenuTrigger>
      <MenuPopup
        align="start"
        className={COMPOSER_STASH_MENU_POPUP_CLASS}
        {...composerFloatingLayerProps}
      >
        <ComposerStashMenuItems {...props} />
      </MenuPopup>
    </Menu>
  );
}

export function ComposerStashMenuItems(props: ComposerStashControlProps) {
  const entries = useStore(promptStashStore, (state) => state.entries);
  return (
    <>
      <MenuItem disabled={props.disabled} onClick={props.adapter.save}>
        <BookmarkPlusIcon className="size-4" />
        Stash current prompt
        {props.shortcutLabel ? <MenuShortcut>{props.shortcutLabel}</MenuShortcut> : null}
      </MenuItem>
      {entries.length > 0 ? <MenuSeparator /> : null}
      {entries.map((entry) => (
        <StashedPromptActions key={entry.id} entry={entry} {...props} />
      ))}
    </>
  );
}

function StashedPromptActions({
  entry,
  adapter,
  onResult,
  disabled,
}: Pick<ComposerStashControlProps, "adapter" | "onResult" | "disabled"> & {
  readonly entry: PromptStashEntry;
}) {
  const preview = entry.prompt.trim() || "Attachments and context";
  return (
    <MenuGroup className="min-w-0 border-border/60 border-b pb-1 last:border-b-0">
      <MenuGroupLabel className="w-full min-w-0 truncate pt-1.5 pb-1">{preview}</MenuGroupLabel>
      <div className="grid min-w-0 grid-cols-2 gap-1 px-1">
        <MenuItem
          disabled={disabled}
          aria-label={`Restore ${preview}`}
          onClick={() => adapter.restore(entry)}
        >
          <ArchiveRestoreIcon className="size-4" />
          Restore
        </MenuItem>
        <MenuItem
          disabled={disabled}
          aria-label={`Delete ${preview}`}
          variant="destructive"
          onClick={() => {
            const result = promptStashStore.getState().remove(entry.id);
            if (!result.ok) onResult("Could not delete the stashed prompt", "error");
            if (result.ok) {
              void Promise.all(
                (entry.files ?? []).map((file) => releaseUnreferencedComposerFile(file.id)),
              );
            }
          }}
        >
          <Trash2Icon className="size-4" />
          Delete
        </MenuItem>
      </div>
    </MenuGroup>
  );
}
